import React, { forwardRef, useState, useRef, useEffect } from 'react'
import classNames from 'classnames'
import ReactTags from 'react-tag-autocomplete'
import { BaseInputStyle, InputWrapperStyle } from './style'
import './form.css'
import Select from 'react-select'
import tailwindConfig from '../../../../../tailwind.config'
import Editor from '@components/Editor'

const Form = (props) => {
    return <form {...props}>{props.children}</form>
}

Form.Group = (props) => {
    const className = classNames('flex', props.horizontal ? 'row' : 'column')
    return <div className={className}>{props.children}</div>
}

Form.Label = (props) => {
    const className = classNames('block font-bold mb-1')
    return (
        <label className={className} {...props}>
            {props.children}
        </label>
    )
}

Form.ImageInput = ({
    handleImageChange,
    id,
    imagePreviewUrl,
    inputKey,
    name,
    onDelete,
    inputRef,
}) => {
    return (
        <>
            <input
                name={name}
                id={id}
                type={'file'}
                ref={inputRef}
                accept={'.jpg, .png, .gif'}
                onChange={handleImageChange}
                key={inputKey}
            />
            <div className={'relative w-max'}>
                {!imagePreviewUrl && (
                    <label htmlFor={id} className={'custom-upload'} />
                )}
                {imagePreviewUrl && (
                    <>
                        <img
                            src={imagePreviewUrl}
                            className={'custom-upload object-cover'}
                        />
                        {/* We need type like below instead of Button-component, to not trigger form submission */}
                        <input
                            className={'custom-upload-delete'}
                            type='button'
                            onClick={onDelete}
                        />
                        <div className={'cross'} />
                    </>
                )}
            </div>
        </>
    )
}

// @TODO, style this one
Form.ImageUpload = forwardRef((props, ref) => {
    const [imagePreviewUrl, setImagePreviewUrl] = useState(
        props.imagePreviewUrl ? props.imagePreviewUrl : null
    )
    const [inputKey, setInputKey] = useState(Date.now())

    const handleImageChange = (e) => {
        e.preventDefault()
        let reader = new FileReader()
        let file = e.target.files[0]

        reader.onloadend = () => {
            setImagePreviewUrl(reader.result)
        }

        if (file) {
            reader.readAsDataURL(file)
        } else {
            setImagePreviewUrl(null)
        }
    }
    return (
        <Form.ImageInput
            name={props.name}
            id={props.id}
            inputRef={ref}
            accept={'.jpg, .png, .gif'}
            onChange={handleImageChange}
            inputKey={inputKey}
            handleImageChange={handleImageChange}
            imagePreviewUrl={imagePreviewUrl}
            onDelete={() => {
                setInputKey(Date.now())
                setImagePreviewUrl(null)
            }}
        />
    )
})

Form.Input = forwardRef((props, ref) => {
    const className = classNames(BaseInputStyle)
    return <input {...props} className={className} ref={ref} />
})

Form.TextArea = forwardRef((props, ref) => {
    const className = classNames(BaseInputStyle, 'resize-none')
    return (
        <textarea {...props} className={className} ref={ref}>
            {props.children}
        </textarea>
    )
})

Form.Select = forwardRef((props, ref) => {
    const { options } = props
    const className = classNames(BaseInputStyle)
    return (
        <select className={className} {...props} ref={ref}>
            {options.map((option, index) => {
                return (
                    <option key={index} value={option.value} disabled={option.disabled}>
                        {option.name}
                    </option>
                )
            })}
        </select>
    )
})

Form.MultiSelect = (props) => {
    const {
        options,
        name,
        onChange,
        onInputChange,
        value,
        defaultValue,
        placeholder = 'Välj',
        components = {},
        maxWidth,
        styles = {},
        hideSelectedOptions = true,
        isDisabled = false,
        isMulti = false,
        isSearchable = false,
        noOptionsMessage = 'Inget resultat'
    } = props

    return (
        <Select
            value={value}
            defaultValue={defaultValue}
            placeholder={placeholder}
            name={name}
            onChange={onChange}
            onInputChange={onInputChange}
            options={options}
            components={{
                IndicatorSeparator: null,
                ...components,
            }}
            getOptionLabel={option => option.name}
            styles={{
                option: (provided, state) => ({
                    ...provided,
                    color: 'black',
                    backgroundColor:
                    state.isFocused || state.isSelected
                        ? tailwindConfig.theme.colors.secondaryGreen
                        : 'white',
                    '&:hover': { backgroundColor: tailwindConfig.theme.colors.secondaryGreen },
                }),
                dropdownIndicator: (provided) => ({
                    ...provided,
                    color: '#6b7280',
                }),
                control: (provided) => ({
                    ...provided,
                    padding: '4px',
                    borderColor: '#D6D6D6',
                }),
                menuList: (provided) => ({
                    ...provided,
                    paddingTop: 0,
                    paddingBottom: 0,
                }),
                ...styles,
            }}
            controlShouldRenderValue={true}
            isOptionDisabled={(option) => option.disabled}
            className={`multiSelect w-full ${maxWidth ? `max-w-[${maxWidth}]` : ''}`}
            noOptionsMessage={() => noOptionsMessage}
            // closeMenuOnSelect={false}
            hideSelectedOptions={hideSelectedOptions}
            isClearable={false}
            isSearchable={isSearchable}
            isMulti={isMulti}
            isDisabled={isDisabled}
        />
    )
}

Form.Radio = forwardRef((props, ref) => {
    const className = classNames('flex cursor-pointer')
    const inputWrapperClass = classNames(InputWrapperStyle)
    return (
        <label htmlFor={props.id} className={className}>
            <div className={inputWrapperClass}>
                <input
                    type={'radio'}
                    {...props}
                    ref={ref}
                    className={'mr-2 opacity-0'}
                />
                <span className={'checkmark'} style={props.style}/>
            </div>

            {props.label && <span className={'label'}>{props.label}</span>}
        </label>
    )
})

Form.QuizRadio = forwardRef((props, ref) => {
    // Pull custom variables off props
    const { markCorrect, markIncorrect, ...rest } = props
    const className = classNames('flex mt-[3px]', {['cursor-pointer']: !props.disabled})
    const inputWrapperClass = classNames(InputWrapperStyle)

    return (
        <label htmlFor={props.id} className={className}>
            <div className={inputWrapperClass}>
                <input
                    type={'radio'}
                    {...rest}
                    ref={ref}
                    className={'mr-2 opacity-0'}
                />
                {!markCorrect && !markIncorrect && (
                    <span className={'checkmark'} />
                )}
                {markCorrect && <span className={'checkmark-correct'} />}
                {markIncorrect && <span className={'checkmark-incorrect'} />}
            </div>

            {props.label && <span className={'label'}>{props.label}</span>}
        </label>
    )
})

Form.Checkbox = forwardRef((props, ref) => {
    const className = classNames('flex cursor-pointer')
    const inputWrapperClass = classNames(InputWrapperStyle)
    return (
        <label htmlFor={props.id} className={className}>
            <div className={inputWrapperClass}>
                <input
                    type={'checkbox'}
                    {...props}
                    ref={ref}
                    className={'mr-2 opacity-0'}
                />
                <span className={'checkmark'} />
            </div>

            {props.label && <span className={'label'}>{props.label}</span>}
        </label>
    )
})

Form.QuizCheckbox = forwardRef((props, ref) => {
    // Pull custom variables off props
    const { markCorrect, markIncorrect, ...rest } = props
    const className = classNames('flex mt-[3px]', {['cursor-pointer']: !props.disabled})
    const inputWrapperClass = classNames(InputWrapperStyle)

    return (
        <label htmlFor={props.id} className={className}>
            <div className={inputWrapperClass}>
                <input
                    type={'checkbox'}
                    {...rest}
                    ref={ref}
                    className={'mr-2 opacity-0'}
                />
                {!markCorrect && !markIncorrect && (
                    <span className={'checkmark'} />
                )}
                {markCorrect && <span className={'checkmark-correct'} />}
                {markIncorrect && <span className={'checkmark-incorrect'} />}
            </div>

            {props.label && <span className={'label'}>{props.label}</span>}
        </label>
    )
})

Form.Tags = (props) => {
    const { onChange, tags: propsTags, suggestions: propsSuggestions } = props

    const [tags, setTags] = useState(propsTags)
    const [suggestions, setSuggestions] = useState(propsSuggestions)

    const onDelete = (i) => {
        let modifiedTags = tags.slice(0)
        modifiedTags.splice(i, 1)
        setTags(modifiedTags)
    }

    const onAddition = (tag) => {
        if(tag.hasOwnProperty('id') && tag.id === undefined) delete tag.id
      
        let modifiedTags = [...tags, tag]
        setTags(modifiedTags)
    }

    const reactTagsRef = useRef()

    useEffect(() => {
        onChange(tags)
    }, [onChange, tags])

    return (
        <ReactTags
            ref={reactTagsRef}
            tags={tags}
            suggestions={suggestions}
            onDelete={(i) => {
                onDelete(i)
            }}
            onAddition={(tag) => {
                onAddition(tag)
            }}
            allowNew
            addOnBlur
            placeholderText={'Lägg till tagg'}
        />
    )
}

Form.Editor = (props) => {
  const {
    elementId,
    handleChange,
    content,
    placeholder,
  } = props
  const editorRef = useRef()
  const [ready, setReady] = useState(false)

  const onChange = async () => {
    const currentContent = await editorRef.current.getContent()

    handleChange(JSON.stringify(currentContent))
  }

  return (
    <div className={'border border-mediumGrey rounded py-2.5 px-3 ' + (ready ? '' : 'hidden')}>
      <Editor
        ref={editorRef}
        content={content}
        onChange={onChange}
        onReady={() => setReady(true)}
        elementId={elementId}
        exercises={false}
        autofocus={false}
        minHeight={0}
        placeholder={placeholder}
      />
    </div>
  )
}

Form.ErrorMessage = (props) => {
    const className = classNames('my-1 text-sm text-errorRed')
    return <p className={className}>{props.children}</p>
}

export default Form
